import { useCallback, useEffect, useRef } from 'react';

import { useParceirosControllerRegistrarEvento } from '@/api/generated/parceiros/parceiros';

/** Fração do card visível para contar impressão. */
const VISIBILIDADE_MINIMA = 0.5;
/** Tempo contínuo visível antes de contar. Passar o olho rolando não conta. */
const MS_ATE_CONTAR = 1000;

/**
 * Impressão e clique de parceiro (B8-9).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  Bem mais simples que o da V1 (`useParceiroTracking.ts`, 230 linhas), e de
 *  propósito: lá o hook carregava um buffer, um `setInterval` de 5 s, um
 *  `beforeunload` com `sendBeacon` e retentativa manual, porque ele escrevia
 *  DIRETO na tabela pelo supabase-js e precisava fingir ser um backend.
 *
 *  Aqui a dedupe por janela e o throttle vivem no servidor, então o cliente só
 *  precisa avisar. Um `Set` local evita o request repetido na mesma sessão de
 *  tela; o resto é problema de quem recebe.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export function useTrackingParceiro() {
  const registrar = useParceirosControllerRegistrarEvento();

  /** Já avisados nesta montagem — evita o request, não a contagem. */
  const jaAvisados = useRef(new Set<string>());
  const temporizadores = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const observador = useRef<IntersectionObserver | null>(null);
  const elementos = useRef(new Map<string, Element>());

  // `useRef` na mutation: o `observe` é montado uma vez, e capturar o
  // `registrar` daquele render deixaria o callback preso à primeira closure.
  const registrarRef = useRef(registrar);
  registrarRef.current = registrar;

  const enviar = useCallback((parceiroId: string, tipo: 'VISUALIZACAO' | 'CLIQUE') => {
    const chave = `${parceiroId}:${tipo}`;
    if (jaAvisados.current.has(chave)) return;
    jaAvisados.current.add(chave);

    // Falha de telemetria não pode virar erro na tela: o médico não tem o que
    // fazer com "não consegui contar a impressão".
    registrarRef.current.mutate({ id: parceiroId, data: { tipo } }, { onError: () => undefined });
  }, []);

  useEffect(() => {
    const temporizadoresAtuais = temporizadores.current;

    observador.current = new IntersectionObserver(
      (entradas) => {
        for (const entrada of entradas) {
          const id = entrada.target.getAttribute('data-parceiro-id');
          if (!id) continue;

          if (entrada.isIntersecting && entrada.intersectionRatio >= VISIBILIDADE_MINIMA) {
            if (temporizadoresAtuais.has(id)) continue;
            temporizadoresAtuais.set(
              id,
              setTimeout(() => {
                temporizadoresAtuais.delete(id);
                enviar(id, 'VISUALIZACAO');
              }, MS_ATE_CONTAR),
            );
            continue;
          }

          // Saiu de vista antes de completar o tempo: não conta.
          const pendente = temporizadoresAtuais.get(id);
          if (pendente) {
            clearTimeout(pendente);
            temporizadoresAtuais.delete(id);
          }
        }
      },
      { threshold: VISIBILIDADE_MINIMA },
    );

    return () => {
      observador.current?.disconnect();
      for (const t of temporizadoresAtuais.values()) clearTimeout(t);
      temporizadoresAtuais.clear();
    };
  }, [enviar]);

  /** `ref` do card. Passe como `ref={refDeImpressao(parceiro.id)}`. */
  const refDeImpressao = useCallback(
    (parceiroId: string) => (elemento: HTMLElement | null) => {
      const anterior = elementos.current.get(parceiroId);
      if (anterior) observador.current?.unobserve(anterior);

      if (!elemento) {
        elementos.current.delete(parceiroId);
        return;
      }

      elemento.setAttribute('data-parceiro-id', parceiroId);
      elementos.current.set(parceiroId, elemento);
      observador.current?.observe(elemento);
    },
    [],
  );

  const registrarClique = useCallback(
    (parceiroId: string) => enviar(parceiroId, 'CLIQUE'),
    [enviar],
  );

  return { refDeImpressao, registrarClique };
}
